import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ministryService } from '../../services/ministry-service';
import { eventService } from '../../services/event-service';
import { schoolService } from '../../services/school-service';
import { cn } from '../../utils/class-names-util';
import { Language } from '../../constants/language';
import { productService } from '../../services/product-service';
import { getMerchantName, ProductType, getProductImageUrl } from '../../models/product/product';

interface SearchResult {
	id: number;
	name: string;
	description: string;
	type: 'ministry' | 'book' | 'event' | 'school';
	logo?: string;
	coverImage?: string;
	image?: string;
	author?: string;
	category: string;
	link: string;
	location?: string;
	date?: string;
}

interface SearchBarProps {
	isScrolled?: boolean;
	isMobile?: boolean;
	className?: string;
	expandable?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
	isScrolled = false,
	isMobile = false,
	className,
	expandable = false
}) => {
	const { t, i18n } = useTranslation();
	const currentLanguage = i18n.language as Language;
	const [searchQuery, setSearchQuery] = useState('');
	const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);
	const searchRef = useRef<HTMLDivElement>(null);

	// Fetch and filter ministries, books, events and schools based on search query
	useEffect(() => {
		const searchContent = async () => {
			if (searchQuery.trim().length < 1) {
				setSearchResults([]);
				setIsSearchOpen(false);
				return;
			}

			setIsLoading(true);
			try {
				// Search ministries, products, events, and schools in parallel for performance
				const [ministriesResult, products, eventsResult, schoolsResult] = await Promise.all([
					ministryService.getMinistries(),
					productService.searchProducts(searchQuery, currentLanguage, ProductType.BOOK),
					eventService.getEvents(currentLanguage),
					schoolService.getSchools(currentLanguage)
				]);

				const ministries = ministriesResult.data;
				const events = eventsResult.data;
				const schools = schoolsResult.data;

				const lowerQuery = searchQuery.toLowerCase();

				// Transform ministries to search results
				const ministryResults: SearchResult[] = ministries
					.filter(ministry =>
						ministry.title.toLowerCase().includes(lowerQuery) ||
						ministry.description.toLowerCase().includes(lowerQuery) ||
						ministry.category.toLowerCase().includes(lowerQuery)
					)
					.map(ministry => ({
						id: ministry.id,
						name: ministry.title,
						description: ministry.description,
						type: 'ministry' as const,
						logo: ministry.media.logo?.url || '',
						category: ministry.category,
						link: `/ministries/${ministry.id}`
					}));

				// Transform books to search results
				const productResults: SearchResult[] = products.map(product => ({
					id: product.id,
					name: product.name,
					description: product.description || t('search.no-description', 'No description available'),
					type: 'book' as const,
					coverImage: getProductImageUrl(product),
					author: getMerchantName(product),
					category: product.category,
					link: `/store?product=${product.id}`
				}));

				// Transform events to search results
				const eventResults: SearchResult[] = events
					.filter(event =>
						event.title.toLowerCase().includes(lowerQuery) ||
						event.description.toLowerCase().includes(lowerQuery) ||
						event.shortDescription.toLowerCase().includes(lowerQuery) ||
						event.address.name.toLowerCase().includes(lowerQuery) ||
						event.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
					)
					.map(event => ({
						id: event.id,
						name: event.title,
						description: event.shortDescription,
						type: 'event' as const,
						image: event.media.cover?.url || '',
						category: event.category,
						link: `/events/${event.key}/${event.id}`,
						location: event.address.name,
						date: event.startDate
					}));

				// Transform schools to search results
				const schoolResults: SearchResult[] = schools
					.filter(school =>
						school.title.toLowerCase().includes(lowerQuery) ||
						school.description.toLowerCase().includes(lowerQuery) ||
						school.shortDescription.toLowerCase().includes(lowerQuery) ||
						school.category.toLowerCase().includes(lowerQuery)
					)
					.map(school => ({
						id: school.id,
						name: school.title,
						description: school.shortDescription,
						type: 'school' as const,
						image: school.media.cover?.url || '',
						category: school.category,
						link: `/schools/${school.key}/${school.id}`
					}));

				const combinedResults = [
					...ministryResults.slice(0, 2),
					...eventResults.slice(0, 2),
					...schoolResults.slice(0, 2),
					...productResults.slice(0, 2)
				].slice(0, 8);

				setSearchResults(combinedResults);
				setIsSearchOpen(true);
			} catch (error) {
				console.error('Error searching content:', error);
				setSearchResults([]);
			} finally {
				setIsLoading(false);
			}
		};

		const debounceTimer = setTimeout(searchContent, 300);
		return () => clearTimeout(debounceTimer);
	}, [searchQuery, currentLanguage, t]);

	// Handle click outside to close dropdown and collapse if expandable
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
				setIsSearchOpen(false);
				if (expandable && !searchQuery) {
					setIsExpanded(false);
				}
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [expandable, searchQuery]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchQuery(value);
	};

	const handleResultClick = () => {
		setIsSearchOpen(false);
		setSearchQuery('');
		if (expandable) {
			setIsExpanded(false);
		}
	};

	const handleIconClick = () => {
		if (expandable) {
			setIsExpanded(!isExpanded);
		}
	};

	const searchBarClasses = cn(
		"relative",
		className
	);

	const inputClasses = cn(
		"bg-transparent outline-none border-0 border-b pb-1 text-sm transition-all duration-300 focus:outline-none focus:ring-0 focus:shadow-none",
		isMobile ? "w-full" : "w-40",
		isScrolled 
			? "text-black placeholder-gray-500 border-black focus:border-black" 
			: "text-white placeholder-gray-300 border-white focus:border-white"
	);

	const searchIcon = (
		<svg
			className={cn(
				"w-4 h-4 transition-colors duration-200",
				isScrolled 
					? "text-black hover:text-gray-800" 
					: "text-white hover:text-gray-200"
			)}
			fill="none"
			stroke="currentColor"
			viewBox="0 0 24 24"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
			/>
		</svg>
	);

	const dropdownClasses = cn(
		"absolute top-full mt-2 bg-white shadow-2xl rounded-xl border border-gray-200 overflow-hidden z-[9999] transition-all duration-300 ease-in-out transform origin-top",
		// Responsive width: full width on mobile, fixed width on larger screens
		"w-full sm:w-96 md:w-[400px] lg:w-[450px]",
		// Position: left-aligned on mobile, right-aligned on larger screens if needed
		"left-0 sm:left-auto sm:right-0",
		isSearchOpen 
			? "opacity-100 scale-y-100 translate-y-0 pointer-events-auto" 
			: "opacity-0 scale-y-95 -translate-y-2 pointer-events-none"
	);

	return (
		<div ref={searchRef} className={searchBarClasses}>
			{expandable ? (
				// Expandable search bar (desktop)
				<div className="flex items-center">
					{isExpanded ? (
						<div className="flex items-center gap-2 transition-all duration-300 ease-in-out">
							{searchIcon}
							<input
								type="text"
								value={searchQuery}
								onChange={handleInputChange}
								placeholder={t('search.placeholder', 'Search...')}
								className={cn(inputClasses, "animate-in slide-in-from-right-2 duration-300")}
								autoFocus
							/>
						</div>
					) : (
						<button
							onClick={handleIconClick}
							className={cn(
								"p-1 rounded transition-all duration-200 hover:scale-105",
								isScrolled 
									? "hover:bg-gray-100" 
									: "hover:bg-white/20"
							)}
							aria-label="Search"
						>
							{searchIcon}
						</button>
					)}
				</div>
			) : (
				// Normal search bar (mobile/always visible)
				<div className="flex items-center gap-2">
					{searchIcon}
					<input
						type="text"
						value={searchQuery}
						onChange={handleInputChange}
						placeholder={t('search.placeholder', 'Search...')}
						className={inputClasses}
					/>
				</div>
			)}

			{/* Search Results Dropdown */}
			<div className={dropdownClasses}>
				{isLoading ? (
					<div className="p-6 text-center text-gray-500 animate-in fade-in duration-200">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto mb-2"></div>
						<p className="text-sm">{t('search.searching', 'Searching...')}</p>
					</div>
				) : searchResults.length > 0 ? (
					<div className="max-h-[400px] overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-300">
						<div className="p-2 text-xs text-gray-500 font-medium border-b border-gray-100">
							{t('search.found-results', 'Found {{count}} result{{plural}}', {
								count: searchResults.length,
								plural: searchResults.length !== 1 ? 's' : ''
							})}
						</div>
						{searchResults.map((result) => (
							<Link
								key={`${result.type}-${result.id}`}
								to={result.link}
								onClick={handleResultClick}
								className="block hover:bg-gray-50 transition-all duration-200 hover:shadow-sm hover:scale-[1.01]"
							>
								<div className="p-4 flex items-start gap-3 border-b border-gray-100 last:border-b-0">
									<div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg overflow-hidden">
										{result.type === 'ministry' ? (
											<img
												src={result.logo}
												alt={result.name}
												className="w-full h-full object-cover"
												onError={(e) => {
													const target = e.target as HTMLImageElement;
													target.style.display = 'none';
													const parent = target.parentElement;
													if (parent) {
														parent.innerHTML = `<div class="w-full h-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs font-bold">${result.name.charAt(0)}</div>`;
													}
												}}
											/>
										) : result.type === 'book' ? (
											<img
												src={result.coverImage}
												alt={result.name}
												className="w-full h-full object-cover"
												onError={(e) => {
													const target = e.target as HTMLImageElement;
													target.style.display = 'none';
													const parent = target.parentElement;
													if (parent) {
														parent.innerHTML = '<div class="w-full h-full bg-gray-200 flex items-center justify-center"><svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg></div>';
													}
												}}
											/>
										) : result.image ? (
											<img
												src={result.image}
												alt={result.name}
												className="w-full h-full object-cover"
												onError={(e) => {
													const target = e.target as HTMLImageElement;
													target.style.display = 'none';
													const parent = target.parentElement;
													if (parent) {
														const icon = result.type === 'event'
															? '<svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>'
															: '<svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>';
														parent.innerHTML = `<div class="w-full h-full bg-gray-200 flex items-center justify-center">${icon}</div>`;
													}
												}}
											/>
										) : (
											<div className="w-full h-full bg-gray-200 flex items-center justify-center">
												{result.type === 'event' ? (
													<svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
													</svg>
												) : (
													<svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
													</svg>
												)}
											</div>
										)}
									</div>

									<div className="flex-1">
										<div className="flex items-center space-x-2 mb-1">
											<h4 className="font-medium text-gray-900 text-sm line-clamp-1">
												{result.name}
											</h4>
											<span className={`inline-flex text-xs px-2 py-0.5 rounded-full font-medium ${
												result.type === 'ministry' 
													? 'bg-blue-100 text-blue-700' 
													: result.type === 'book'
														? 'bg-green-100 text-green-700'
														: result.type === 'event'
															? 'bg-purple-100 text-purple-700'
															: 'bg-orange-100 text-orange-700'
											}`}>
												{result.type === 'ministry' 
													? t('search.type.ministry', 'Ministry') 
													: result.type === 'book'
														? t('search.type.book', 'Book')
														: result.type === 'event'
															? t('search.type.event', 'Event')
															: t('search.type.school', 'School')
												}
											</span>
										</div>
										{result.author && (
											<p className="text-xs text-gray-500 mb-1">
												{t('products.books.by-author', 'by')} {result.author}
											</p>
										)}
										{result.location && (
											<p className="text-xs text-gray-500 mb-1 flex items-center">
												<svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
												</svg>
												{result.location}
											</p>
										)}
										{result.date && (
											<p className="text-xs text-gray-500 mb-1 flex items-center">
												<svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
												</svg>
												{new Date(result.date).toLocaleDateString(currentLanguage)}
											</p>
										)}
										<p className="text-xs text-gray-600 line-clamp-2 mb-2">
											{result.description}
										</p>
										<span className="inline-block mt-2 text-xs px-2 py-1 bg-gray-800 text-white rounded-full">
											{result.type === 'book' 
												? t(`books.categories.${result.category}`, result.category)
												: result.type === 'event'
													? t(`pages.events.categories.${result.category}`, result.category)
													: result.category
											}
										</span>
									</div>
								</div>
							</Link>
						))}
					</div>
				) : searchQuery.trim().length > 0 ? (
					<div className="p-4 sm:p-6 text-center text-gray-500 animate-in fade-in slide-in-from-bottom-2 duration-300">
						<svg
							className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 text-gray-300"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
							/>
						</svg>
						<p className="font-medium text-gray-900 mb-1 text-sm sm:text-base">
							{t('search.no-results', 'No results found')}
						</p>
						<p className="text-xs sm:text-sm text-gray-500">
							{t('search.try-different', 'Try a different search term')}
						</p>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default SearchBar;
